import { useLocation } from 'react-router-dom';

import { appConfig } from '../../../appConfig';
import { Cookies } from '../../../utility/cookies';
import { BUSINESS_TOGGLE_COOKIE } from '../persistance/cookie/cookieKeys';
import { trimSlashes } from './stringUtils';

export const B2B_SEGMENT = appConfig.coopSettings.b2b.startPage
    ? trimSlashes(appConfig.coopSettings.b2b.startPage.url)
    : 'notsetup';

export const isB2BMode = () => {
    if (appConfig.coopSettings.featureFlags.enableB2B) {
        return isB2BWindowLocation();
    }

    return Cookies.read(BUSINESS_TOGGLE_COOKIE);
};

const getIsRouteB2B = (pathName: string) => {
    const firstSegmentsOfB2Broutes = appConfig.coopSettings.b2b.b2bRoutes
        ?.map(trimSlashes)
        .map((segments) => segments.split('/')[0]);

    const currentPathnameFirstSegment = trimSlashes(pathName).split('/')[0];

    // check only first segments of the urls to automatically include all their child pages
    const isB2BRoute = firstSegmentsOfB2Broutes.some(
        (item) => item.toLowerCase() === currentPathnameFirstSegment.toLowerCase(),
    );
    return isB2BRoute;
};

export const isB2BWindowLocation = () => {
    return getIsRouteB2B(window.location.pathname);
};

export const useIsB2BRoute = () => {
    const isWindowB2BRoute = isB2BWindowLocation();
    const isRouterB2BRoute = getIsRouteB2B(useLocation().pathname);

    return isWindowB2BRoute || isRouterB2BRoute;
};

type NavNodeWithUrl = {
    id: number;
    url: string;
    segmentUrl: string;
};

type NavNodeWithAncestors<T extends NavNodeWithUrl> = T & {
    ancestors: T[];
};

/**
 * Shared pages (e.g. Mitt Coop) still descend from the B2C root in CMS; navigation and
 * breadcrumbs should link company users to the company homepage instead.
 */
export const replaceB2CStartPageWithB2BHomepage = <T extends NavNodeWithUrl>(
    node: NavNodeWithAncestors<T>,
    isCompany: boolean,
): NavNodeWithAncestors<T> => {
    if (!isCompany) {
        return node;
    }

    const b2bStartPage = appConfig.coopSettings.b2b.startPage;
    const b2cStartPageId = appConfig.coopSettings.b2c.startPage.contentId;

    if (!b2bStartPage) {
        return node;
    }

    return {
        ...node,
        ancestors: node.ancestors.map((ancestor) =>
            ancestor.id === b2cStartPageId
                ? { ...ancestor, url: b2bStartPage.url, segmentUrl: b2bStartPage.segmentUrl }
                : ancestor,
        ),
    };
};

/**
 * Cross-context means that a user is on a site that is not intended for their user type.
 * So a private user on the company site or a company user on the private site.
 */
export const isInB2BCrossContext = ({
    isB2BRoute,
    isUserCompany: isCompany,
    isAuthenticated,
}: {
    isB2BRoute: boolean;
    // if the user is a company user
    isUserCompany: boolean;

    isAuthenticated: boolean;
}) => {
    const privateUserOnCompanySite = isAuthenticated && !isCompany && isB2BRoute;

    const companyUserOnPrivateSite = isAuthenticated && isCompany && !isB2BRoute;
    const privateCartOnCompanySite = !isCompany && isB2BRoute;
    return {
        privateUserOnCompanySite,
        /** If the user is anonymous, the cart is always private! */
        privateCartOnCompanySite,
        companyUserOnPrivateSite,
    };
};
